/**
 * CAPA DE LECTURA UNIFICADA DE DOCUMENTOS DE COSTO (Fase 3a de la tabla de Compras).
 *
 * Hoy un documento de costo puede vivir en tres tablas con shapes distintos:
 *   · `facturas_sii_recibidas` — afectas (33), exentas (34) y notas de crédito (61)
 *   · `facturas_externas`      — proveedores extranjeros, carga manual, multi-moneda
 *   · `boletas_honorarios`     — BHE, solo las que computan (`computa` = status 'vigente')
 *
 * Acá se normalizan a un shape común y se derivan DOS EJES INDEPENDIENTES:
 *
 *   1. CLASIFICACIÓN (eje contable / ER) — de `cuenta_er` + `tipo_contable`.
 *   2. PAGO (eje financiero / partida abierta) — de los vínculos formales en
 *      `movimiento_bancario_facturas`, más el saldo FIFO del ledger de proveedor.
 *
 * LA DESCONTAMINACIÓN. Ninguno de los dos ejes se deriva de `estado_vinculacion`, que tiene
 * cuatro significados ortogonales bajo el mismo valor 'vinculada' (regla 10.65) y por eso
 * miente en las dos direcciones. Medido sobre 2026 al 2026-07-19: **6 facturas 'pendiente'
 * con MBF al 100%** (pagadas formalmente, el flag dice que no) y **56 'vinculada' sin ningún
 * MBF** (el flag afirma un pago que no existe) — 62 de 285 documentos, el 21,8%.
 *
 * ÚNICA lectura admitida de esa columna: el valor `'ignorada'`, que NO es un estado de pago
 * sino el descarte CONTABLE del documento. Vive ahí por historia, y sin leerlo no habría
 * forma de expresar "Ignorada" en el eje de clasificación.
 *
 * Es una capa de LECTURA: no escribe nada, no reemplaza `facturaSiiPorPagar` (el predicado de
 * "Por pagar" de Caja, regla 10.106) ni el motor de costos del ER (`costosER.js`). Ambos
 * siguen siendo la fuente de verdad de lo suyo.
 */
import { CUENTAS_ER } from "../../constants/clasificacionContable";

const EPS = 0.01;
const num = (v) => Number(v) || 0;

const TIPO_DTE_EXENTA = 34;
const TIPO_DTE_NOTA_CREDITO = 61;

/** Origen del documento — también es el prefijo de la clave y de las llaves del ledger. */
export const ORIGEN = { SII: "SII", EXTERNA: "EXT", HONORARIO: "BHE" };

export const TIPO_DOCUMENTO = {
  FACTURA: "Factura",
  EXENTA: "Exenta",
  NOTA_CREDITO: "NC",
  HONORARIO: "Honorario",
  EXTERNA: "Externa",
};

export const CLASIFICACION = {
  SIN_CLASIFICAR: "Sin clasificar",
  CUENTA: "Cuenta",
  ACTIVO: "Activo",
  IGNORADA: "Ignorada",
};

export const PAGO = {
  IMPAGA: "Impaga",
  PARCIAL: "Parcial",
  PAGADA: "Pagada",
  CUBIERTA: "Cubierta a cuenta",
  /** Una NC no es una obligación de pago: reduce deuda, no se paga (regla 10.42). */
  NO_APLICA: "No aplica",
};

/**
 * Ciclo de vida del DTE de compra en el RCV del SII (columna `estado_sii`, migración
 * 20260723 / regla 10.154). Eje independiente del pago y de la clasificación:
 *   · ACEPTADA  → ya en el RCV. Es la ÚNICA que cuenta como PASIVO FIRME.
 *   · PENDIENTE → en obligations/pending, sin acuse de recibo (Ley 19.983; aceptación tácita
 *                 a los 8 días). NO es deuda firme todavía.
 *   · RECHAZADA → rechazo confirmado. Nunca será deuda.
 * Externas y honorarios no tienen ciclo SII → se tratan como ACEPTADA (siempre firmes).
 */
export const ESTADO_SII = {
  ACEPTADA: "aceptada",
  PENDIENTE: "pendiente",
  RECHAZADA: "rechazada",
};

/** true si el documento es PASIVO FIRME (cuenta como deuda por pagar en los totales). */
export const esPasivoFirme = (doc) => (doc?.estadoSii || ESTADO_SII.ACEPTADA) === ESTADO_SII.ACEPTADA;

const LABEL_CUENTA = Object.fromEntries(CUENTAS_ER.map((c) => [c.value, c.label]));

/** Clave única cross-tabla: los ids de las tres tablas son secuencias independientes. */
export const claveDocumento = (origen, id) => `${origen}:${id}`;

// ─── Normalizadores ──────────────────────────────────────────────────────────

const tipoDeSii = (tipoDte) => {
  const t = Number(tipoDte);
  if (t === TIPO_DTE_NOTA_CREDITO) return TIPO_DOCUMENTO.NOTA_CREDITO;
  if (t === TIPO_DTE_EXENTA) return TIPO_DOCUMENTO.EXENTA;
  return TIPO_DOCUMENTO.FACTURA;
};

/**
 * @param f fila cruda de facturas_sii_recibidas
 * @param {Map<number,string>} [proveedoresPorId] id → nombre del proveedor vinculado. Cuando la
 *   factura tiene proveedor_id, el nombre a mostrar sale de acá (el proveedor identificado), no
 *   de la razón social suelta del SII — así todas las facturas de un mismo proveedor se agrupan
 *   bajo un nombre (Entel), aunque cada DTE traiga una razón social distinta ("Entel Pcs"/"PCS").
 *   Fallback a razon_social_emisor si no hay proveedor_id o el proveedor no tiene nombre.
 */
export const normalizarSiiRecibida = (f, proveedoresPorId = null) => {
  const nombreVinculado = proveedoresPorId && f.proveedor_id != null ? proveedoresPorId.get(f.proveedor_id) : null;
  return {
    clave: claveDocumento(ORIGEN.SII, f.id),
    origen: ORIGEN.SII,
    id: f.id,
    pais: f.pais,
    tipo: tipoDeSii(f.tipo_dte),
    tipoDte: Number(f.tipo_dte) || null,
    // La NC RESTA: viene guardada en positivo, igual que en costosER (regla 10.42).
    signo: Number(f.tipo_dte) === TIPO_DTE_NOTA_CREDITO ? -1 : 1,
    proveedorId: f.proveedor_id ?? null,
    proveedorNombre: nombreVinculado || f.razon_social_emisor || "—",
    // Razón social del SII (nombre suelto de la factura). Se conserva SOLO para el tooltip que
    // distingue el DTE original cuando el nombre mostrado es el del proveedor vinculado. No es
    // fuente de lógica de negocio (regla 8: comparar por id, no por nombre).
    razonSocial: f.razon_social_emisor || null,
    proveedorRut: f.rut_emisor || null,
    fecha: f.fecha_emision ? String(f.fecha_emision).split("T")[0] : null,
    folio: f.folio != null ? String(f.folio) : null,
    neto: num(f.monto_neto),
    exento: num(f.monto_exento),
    iva: num(f.monto_iva),
    total: num(f.monto_total),
    // `facturas_sii_recibidas` NO tiene columna moneda: el SII siempre reporta en local.
    moneda: null,
    cuentaEr: f.cuenta_er || null,
    tipoContable: f.tipo_contable || null,
    // Origen de la clasificación: true = excepción puesta a mano (override, O-1). El modal
    // per-factura lo usa para mostrar "de dónde viene" y ofrecer "volver a la regla".
    clasificacionManual: Boolean(f.clasificacion_manual),
    // Se conserva CRUDO solo para trazabilidad/auditoría. Ningún eje lo deriva de acá salvo
    // el valor 'ignorada' del eje contable (ver `derivarClasificacion`).
    estadoVinculacionCrudo: f.estado_vinculacion || null,
    // Ciclo de vida SII (RCV). Default 'aceptada' para filas viejas sin la columna (backfill).
    estadoSii: f.estado_sii || ESTADO_SII.ACEPTADA,
    // Fila ORIGINAL: los modales de edición (AsignarRecibidaModal y compañía) esperan el shape
    // de su tabla, no el normalizado. Se adosa para no tener que re-fetchear al abrirlos.
    raw: f,
  };
};

export const normalizarExterna = (f) => ({
  clave: claveDocumento(ORIGEN.EXTERNA, f.id),
  origen: ORIGEN.EXTERNA,
  id: f.id,
  pais: f.pais,
  tipo: TIPO_DOCUMENTO.EXTERNA,
  tipoDte: null,
  signo: 1,
  proveedorId: f.proveedor_id ?? null,
  proveedorNombre: f.proveedor_nombre || "—",
  proveedorRut: null,
  fecha: f.fecha_emision ? String(f.fecha_emision).split("T")[0] : null,
  folio: f.numero_documento || null,
  neto: num(f.monto_neto),
  exento: 0,
  iva: num(f.monto_impuesto),
  total: num(f.monto_total),
  moneda: f.moneda || null,
  cuentaEr: f.cuenta_er || null,
  // `facturas_externas` no tiene `tipo_contable`: no hay path externa→activo.
  tipoContable: null,
  clasificacionManual: false,
  estadoVinculacionCrudo: null,
  // Sin ciclo SII: una externa siempre es firme.
  estadoSii: ESTADO_SII.ACEPTADA,
  raw: f,
});

export const normalizarHonorario = (b) => ({
  clave: claveDocumento(ORIGEN.HONORARIO, b.id),
  origen: ORIGEN.HONORARIO,
  id: b.id,
  pais: b.pais,
  tipo: TIPO_DOCUMENTO.HONORARIO,
  tipoDte: null,
  signo: 1,
  proveedorId: b.proveedor_id ?? null,
  proveedorNombre: b.emisor_nombre || "—",
  proveedorRut: b.emisor_rut || null,
  fecha: b.fecha_emision ? String(b.fecha_emision).split("T")[0] : null,
  folio: b.folio != null ? String(b.folio) : null,
  // La BHE es EXENTA de IVA (segunda categoría): el bruto ES el costo, sin neto ni IVA.
  neto: 0,
  exento: num(b.monto_bruto),
  iva: 0,
  total: num(b.monto_bruto),
  moneda: null,
  cuentaEr: b.cuenta_er || null,
  tipoContable: b.tipo_contable || null,
  clasificacionManual: Boolean(b.clasificacion_manual),
  estadoVinculacionCrudo: null,
  // Sin ciclo SII: una boleta de honorarios que computa siempre es firme.
  estadoSii: ESTADO_SII.ACEPTADA,
  // La retención NO baja el costo: es un pasivo tributario que la empresa entera al SII
  // en nombre del prestador (vive en el bloque F29 de Caja, regla 7.4).
  retencion: num(b.monto_retencion),
  liquido: num(b.monto_bruto) - num(b.monto_retencion),
  raw: b,
});

// ─── Eje 1 — Clasificación (ER) ──────────────────────────────────────────────

/**
 * Sin clasificar / [Cuenta] / Activo / Ignorada.
 *
 * `'ignorada'` es la ÚNICA lectura de `estado_vinculacion` en toda esta capa, y es legítima:
 * ahí no significa "pagada", significa "descartada contablemente".
 */
export const derivarClasificacion = (doc) => {
  if (doc.estadoVinculacionCrudo === "ignorada") {
    return { estado: CLASIFICACION.IGNORADA, cuenta: null, label: CLASIFICACION.IGNORADA };
  }
  // CAPEX: no es gasto del período, vive en Depreciación. Gana sobre la cuenta porque hay
  // filas con `tipo_contable='activo'` y `cuenta_er=''` (el modal no la limpia).
  if (doc.tipoContable === "activo") {
    return { estado: CLASIFICACION.ACTIVO, cuenta: null, label: CLASIFICACION.ACTIVO };
  }
  // `''` cuenta como sin clasificar igual que NULL (hay filas con string vacío en DB).
  if (!doc.cuentaEr) {
    return { estado: CLASIFICACION.SIN_CLASIFICAR, cuenta: null, label: CLASIFICACION.SIN_CLASIFICAR };
  }
  return {
    estado: CLASIFICACION.CUENTA,
    cuenta: doc.cuentaEr,
    label: LABEL_CUENTA[doc.cuentaEr] || doc.cuentaEr,
  };
};

// ─── Eje 2 — Pago (partida abierta) ──────────────────────────────────────────

/**
 * Impaga / Parcial X% / Pagada / Cubierta a cuenta / No aplica.
 *
 * FORMAL vs CUBIERTA — la distinción que pidió la fase:
 *   · `formal: true`  → hay filas en `movimiento_bancario_facturas` que apuntan al documento.
 *     El pago está atado a ESTE documento y es auditable.
 *   · `formal: false` → no hay MBF, pero el ledger del proveedor dio el documento por saldado
 *     imputándole pagos a cuenta por FIFO (regla 10.160). La plata existe; la atribución al
 *     documento la puso el motor, no una persona.
 *
 * @param {object} doc - Documento normalizado
 * @param {object} ctx
 * @param {Map}   ctx.montoVinculadoPorDocumento - clave → Σ mbf.monto
 * @param {Map}   ctx.saldosFifo                 - clave → saldo pendiente del ledger
 * @param {Set}   ctx.proveedoresConLedger       - ids con ledger calculado (ver abajo)
 * @param {Map}   ctx.cuotasPorDocumento         - clave → filas de plan_pagos_facturas
 */
export const derivarPago = (doc, ctx = {}) => {
  const { montoVinculadoPorDocumento, saldosFifo, proveedoresConLedger, cuotasPorDocumento } = ctx;

  const cuotas = cuotasPorDocumento?.get(doc.clave) || [];
  // El plan de pagos es METADATO, no una fuente de pago: cuando una cuota se paga por banco
  // se crea igual su fila MBF, así que sumarla acá contaría dos veces el mismo movimiento.
  const plan =
    cuotas.length > 0
      ? {
          tieneCuotas: true,
          cuotas: cuotas.length,
          cuotasPagadas: cuotas.filter((c) => c.estado === "pagado").length,
          proximoVencimiento:
            cuotas
              .filter((c) => c.estado === "pendiente" && c.fecha_vencimiento)
              .map((c) => c.fecha_vencimiento)
              .sort()[0] || null,
        }
      : { tieneCuotas: false, cuotas: 0, cuotasPagadas: 0, proximoVencimiento: null };

  const base = { pagado: 0, saldo: 0, porcentaje: 0, formal: false, plan };

  if (doc.tipo === TIPO_DOCUMENTO.NOTA_CREDITO) {
    return { ...base, estado: PAGO.NO_APLICA, label: PAGO.NO_APLICA };
  }

  const total = num(doc.total);
  const vinculado = num(montoVinculadoPorDocumento?.get(doc.clave));

  // Pago REAL vía MBF (movimiento_bancario_facturas): plata atada al documento y auditable.
  // Se evalúa ANTES del gate de firmeza y vale IGUAL para firmes y no firmes: si una factura
  // 'pendiente por aprobar' ya tiene un pago vinculado, el chip Pago debe decir Pagada/Parcial
  // (el eje Estado —pendiente— y el eje Pago —pagada— son ortogonales, ambos dicen la verdad).
  if (vinculado > EPS) {
    if (total <= EPS || vinculado >= total - EPS) {
      return {
        ...base,
        estado: PAGO.PAGADA,
        label: PAGO.PAGADA,
        pagado: vinculado,
        saldo: 0,
        porcentaje: 100,
        formal: true,
      };
    }
    const porcentaje = Math.round((vinculado / total) * 100);
    return {
      ...base,
      estado: PAGO.PARCIAL,
      label: `${PAGO.PARCIAL} ${porcentaje}%`,
      pagado: vinculado,
      saldo: total - vinculado,
      porcentaje,
      formal: true,
    };
  }

  // Sin pago formal (MBF). Recién ACÁ importa la firmeza: una factura no firme (estado_sii
  // 'pendiente'/'rechazada') NO participa del pseudo-estado "Cubierta a cuenta" del ledger —
  // no es deuda exigible todavía. Queda Impaga informativa (y sigue fuera de todo total por
  // esPasivoFirme). Cortar acá evita el falso "Cubierta a cuenta" cuando el ledger no le
  // calculó saldo. Protección de Fase 3 intacta; lo único que cambió es que un pago real de
  // arriba ya no queda oculto.
  if (!esPasivoFirme(doc)) {
    return { ...base, estado: PAGO.IMPAGA, label: PAGO.IMPAGA, saldo: total };
  }

  // El ledger de proveedor puede haberlo dado por saldado vía FIFO:
  // `saldosFifo` SOLO trae las que conservan saldo, así que la AUSENCIA de la clave es la
  // señal de saldada (regla 10.106). Pero la ausencia también ocurre cuando ese proveedor
  // nunca entró al ledger (sin proveedor_id, proveedor-edificio excluido por 10.70, o
  // simplemente no calculado) — por eso hace falta `proveedoresConLedger` para no confundir
  // "saldada" con "no sé".
  const tieneLedger = Boolean(doc.proveedorId) && Boolean(proveedoresConLedger?.has(doc.proveedorId));
  if (tieneLedger) {
    const saldo = saldosFifo?.get(doc.clave);
    // BINARIO a propósito. El saldo del ledger viene en la moneda del PROVEEDOR
    // (`moneda_default`), que no siempre es la del documento: un proveedor USD con facturas
    // SII en CLP deja saldos en USD (el "wobble cross-currency" de 4.2.aa). Comparar ese
    // número contra `doc.total` daría un porcentaje sin sentido, así que solo se usa la señal
    // que NO depende de unidades: hay clave (queda saldo) o no la hay (saldada).
    if (saldo === undefined) {
      return { ...base, estado: PAGO.CUBIERTA, label: PAGO.CUBIERTA, pagado: total, saldo: 0, porcentaje: 100 };
    }
  }

  return { ...base, estado: PAGO.IMPAGA, label: PAGO.IMPAGA, saldo: total };
};

// ─── Orquestador ─────────────────────────────────────────────────────────────

/** Agrupa filas de `movimiento_bancario_facturas` en `clave → Σ monto`. */
export const indexarVinculaciones = (filasMbf) => {
  const porTipo = { sii_recibida: ORIGEN.SII, factura_externa: ORIGEN.EXTERNA };
  const mapa = new Map();
  (filasMbf || []).forEach((m) => {
    const origen = porTipo[m.factura_tipo];
    // `sii_emitida` es de CLIENTES, no de compras: se descarta.
    if (!origen) return;
    const clave = claveDocumento(origen, m.factura_id);
    mapa.set(clave, num(mapa.get(clave)) + num(m.monto));
  });
  return mapa;
};

/** Agrupa `plan_pagos_facturas` (solo aplica a SII recibidas) en `clave → cuotas[]`. */
export const indexarCuotas = (filasPlan) => {
  const mapa = new Map();
  (filasPlan || []).forEach((c) => {
    if (c.factura_sii_recibida_id == null) return;
    const clave = claveDocumento(ORIGEN.SII, c.factura_sii_recibida_id);
    if (!mapa.has(clave)) mapa.set(clave, []);
    mapa.get(clave).push(c);
  });
  return mapa;
};

/**
 * Unifica las tres fuentes y adosa los dos ejes. Función PURA: recibe filas crudas y los
 * índices ya armados, no toca red ni DB.
 *
 * Las boletas de honorarios entran SOLO si computan (`computa` = status 'vigente'); las
 * nula/vca están persistidas para trazabilidad y no son documentos de costo.
 */
export const construirDocumentosCosto = (
  { siiRecibidas = [], externas = [], honorarios = [], proveedores = [] } = {},
  contexto = {}
) => {
  // id → nombre del proveedor vinculado, para etiquetar las SII por proveedor y no por la razón
  // social suelta de cada DTE. Externas y honorarios conservan su propia fuente de nombre.
  const proveedoresPorId = new Map((proveedores || []).filter((p) => p && p.id != null).map((p) => [p.id, p.nombre]));
  const docs = [
    ...siiRecibidas.map((f) => normalizarSiiRecibida(f, proveedoresPorId)),
    ...externas.map(normalizarExterna),
    ...honorarios.filter((b) => b.computa).map(normalizarHonorario),
  ];
  return docs
    .map((doc) => ({ ...doc, clasificacion: derivarClasificacion(doc), pago: derivarPago(doc, contexto) }))
    .sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
};
